import React from 'react';
import { ComponentMethods } from '../ComponentMethods';
import { ComponentProps, PropTableColumns } from '../ComponentProps';
import { Component, Story, Prop, Knobs } from '../../Docs/DocTypes';
import { camelCaseToDash } from '../../Docs/DocsUtils';
import { TagPills } from '@tag/tag-components-react-v2';
import { ComponentEvents } from '../ComponentEvents';
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { KnobValues } from '../CodePreview/KnobsUtils';

export const getReactEventPropName = (eventName: string) =>
  'on' + eventName.charAt(0).toUpperCase() + eventName.slice(1);

export const renderHtmlMethods = (comp: Component) => {
  if (!comp || !comp.methods || comp.methods.length < 1) return;
  return (
    <React.Fragment>
      <H2>Methods</H2>
      <ComponentMethods
        data={comp.methods.map(e => ({
          name: e.name,
          description: e.description,
          parameters: getReactParamString(e.parameters),
          returnType: e.returnType,
        }))}
      />
    </React.Fragment>
  );
};

export const renderHtmlProps = (comp: Component) => (
  <ComponentProps data={comp.props.map(mapPropToTableData)} />
);

export const mapPropToTableData = (prop: Partial<Prop>): PropTableColumns => ({
  property: prop.name || '',
  attribute: camelCaseToDash(prop.name || ''),
  description: prop.description,
  type: prop.type,
  defaultValue: prop.default,
});

export const getReactEventParamString = (params: Prop[]) => {
  let type: string = '(';
  let firstParam: boolean = true;
  if (params) {
    type += '{ detail: {';
    params.forEach(param => {
      if (!firstParam) {
        type += ', ';
      }
      type += `${param.name}:${param.type}`;
      firstParam = false;
    });
    type += '}}';
  }
  type += ')';
  type += ' => void';
  return type;
};

const getReactParamString = (params: Prop[]) => {
  let type: string = '(';
  let firstParam: boolean = true;
  if (params) {
    params.forEach(param => {
      if (!firstParam) {
        type += ', ';
      }
      type += `${param.name}:${param.type}`;
      firstParam = false;
    });
  }
  type += ')';
  return type;
};

export const renderReactProps = (comp: Component) => {
  const props = comp.props.map(mapPropToTableData);
  props.forEach(p => {
    if (
      p.property.length > 5 &&
      p.property.substring(p.property.length - 'Style'.length, p.property.length) === 'Style'
    ) {
      p.type = 'React.CSSProperties';
    }
  });
  comp.reactPropOverrides.forEach(reactOverride => {
    const foundProps = props.filter(p => p.property === reactOverride.name);
    if (foundProps.length > 0) {
      const prop = foundProps[0];
      const mappedReactOverride = mapPropToTableData(reactOverride);

      if (mappedReactOverride.description) {
        prop.description = mappedReactOverride.description;
      }
      if (mappedReactOverride.type) {
        prop.type = mappedReactOverride.type;
      }
      if (mappedReactOverride.defaultValue) {
        prop.defaultValue = mappedReactOverride.defaultValue || '';
      }
    } else {
      props.push(mapPropToTableData(reactOverride));
    }
  });

  comp.events.forEach(e => {
    props.push({
      property: getReactEventPropName(e.name),
      attribute: '',
      description: e.description,
      type: getReactEventParamString(e.parameters),
    });
  });

  return <ComponentProps data={props} hideAttributes />;
};

export const renderHtmlEvents = (comp: Component) => {
  if (!comp || !comp.events || comp.events.length < 1) return;
  return (
    <React.Fragment>
      <H2>Events</H2>
      <ComponentEvents
        data={comp.events.map(e => ({
          name: e.name,
          description: e.description,
          parameters: getReactEventParamString(e.parameters),
        }))}
      />
    </React.Fragment>
  );
};

export function renderPills(
  pills: (
    | { text: string; accent: 'keppel' }
    | { text: string; accent: 'access' }
    | { text: 'alpha' | 'beta'; accent: 'access' }
    | { text: string; accent: 'shiraz' })[],
): React.ReactNode {
  return (
    <div
      css={css`
        margin: -10px 0px 10px;
      `}
    >
      <TagPills pills={pills} />
    </div>
  );
}

export function renderStoryDescription(description?: string): React.ReactNode {
  if (!description) return;
  return (
    <p
      css={css`
        margin: 10px 0px 30px;
        white-space: wrap;
      `}
    >
      {description}
    </p>
  );
}

export function renderComponentDescription(description: string): React.ReactNode {
  if (!description) return;
  return (
    <p
      css={css`
        margin: 0px 0px 30px;
      `}
    >
      {description}
    </p>
  );
}

export function renderComponentNotForMobile(): React.ReactNode {
  return (
    <p
      css={css`
        margin: 0px 0px 30px;
      `}
    >
      <small
        css={css`
          margin: 0px 0px 30px;
        `}
      >
        This component is not for use on a native mobile device
      </small>
    </p>
  );
}

export const H2: React.FC = ({ children }) => (
  <h2
    css={css`
      font-size: 22px;
    `}
  >
    {children}
  </h2>
);

export const getComponentName = (location: any) =>
  new URLSearchParams(location.search).get('component') || '';

export const getSectionName = (location: any) =>
  new URLSearchParams(location.search).get('section') || '';

export const getStoryName = (location: any) =>
  new URLSearchParams(location.search).get('story') || '';

export const getCodeMobileView = (location: any) =>
  (new URLSearchParams(location.search).get('mobileview') || 'false').toLowerCase() === 'true';

export const getCodeVisible = (location: any) =>
  (new URLSearchParams(location.search).get('codevisible') || 'false').toLowerCase() === 'true';

export const getCodeBackgroundVisible = (location: any) =>
  (new URLSearchParams(location.search).get('codebackgroundvisible') || 'false').toLowerCase() ===
  'true';

export type TabName = 'react' | 'html' | 'html_v3' | 'react_v3' | '';

export const getCodeTab = (location: any, story: Story | null): TabName => {
  if (story === null) {
    return '';
  }
  let defaultTab: TabName = '';
  if (story.coreComponent) {
    defaultTab = 'html';
  } else if (story.reactWrapper) {
    defaultTab = 'react';
  } else if (story.coreComponent_v3) {
    defaultTab = 'html_v3';
  } else if (story.reactWrapper_v3) {
    defaultTab = 'react_v3';
  }
  let tab = (new URLSearchParams(location.search).get('codetab') as TabName) || defaultTab;
  if (tab.toLowerCase() === 'html' && !story.coreComponent) {
    tab = 'react';
  }
  if (tab.toLowerCase() === 'react' && !story.reactWrapper) {
    tab = 'html';
  }
  if (tab.toLowerCase() === 'html_v3' && story.coreComponent_v3 && !story.coreComponent_v3.html) {
    tab = 'html';
  }
  return tab;
};

export const getKnobValue = (location: any, knobName: string, urlPrefix: 'html' | 'react') =>
  new URLSearchParams(location.search).get(`knob_${urlPrefix}_${knobName}`);

export const getKnobValuesFromUrl = (location: any, knobs: Knobs, urlPrefix: 'html' | 'react') => {
  const knobValues: KnobValues = {};
  Object.keys(knobs).forEach(knobName => {
    const knobValue = getKnobValue(location, knobName, urlPrefix);
    if (knobValue !== null) {
      knobValues[knobName] = knobValue;
    }
  });
  return knobValues;
};

export const getPath = (
  componentName: string,
  storyName: string,
  codeIsVisible: boolean,
  activeCodeTab: string,
  codeBackgroundIsVisible: boolean,
  mobileView: boolean,
) => {
  let path = `./story?component=${componentName}&story=${storyName}`;
  if (codeIsVisible) {
    path = `${path}&codevisible=true`;
  }
  if (codeBackgroundIsVisible) {
    path = `${path}&codebackgroundvisible=true`;
  }
  if (mobileView) {
    path = `${path}&mobileview=true`;
  }
  if (activeCodeTab) {
    path = `${path}&codetab=${activeCodeTab}`;
  }
  return path;
};
